import React, { useEffect, useState } from "react";
import { api, formatApiError, API_BASE, tokenStorage } from "../lib/api";
import { toast } from "sonner";
import { Award, Download, ExternalLink, AlertTriangle, Loader2, Link as LinkIcon, ClipboardList } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";

/**
 * Blocco in fondo a /studente/corsi/{id}: questionario di gradimento (obbligatorio
 * prima dell'attestato), emissione, download, verifica o banner profilo incompleto.
 */
export default function CertificateBlock({ courseId, finalPassed }) {
  const { user } = useAuth();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emitting, setEmitting] = useState(false);
  const [missing, setMissing] = useState(null);

  const [fb, setFb] = useState(null); // { questions, submitted, final_passed }
  const [ratings, setRatings] = useState([]);
  const [comment, setComment] = useState("");
  const [submittingFb, setSubmittingFb] = useState(false);

  const check = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/certificates");
      const existing = data.find((c) => c.course_id === courseId && !c.is_revoked);
      setCert(existing || null);
      if (!existing) {
        const f = await api.get(`/me/courses/${courseId}/feedback`);
        setFb(f.data);
        setRatings(new Array((f.data.questions || []).length).fill(0));
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  // check() dipende solo da courseId (già nelle deps): l'effetto va rieseguito
  // al cambio di corso o dell'esito finale, non a ogni render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (finalPassed) check(); else setLoading(false); }, [courseId, finalPassed]);

  const missingFields = () => {
    const m = [];
    if (!user?.first_name) m.push("first_name");
    if (!user?.last_name) m.push("last_name");
    if (!user?.fiscal_code) m.push("fiscal_code");
    if (!user?.birth_date) m.push("birth_date");
    return m;
  };

  const submitFeedback = async () => {
    if (ratings.some((r) => r < 1)) {
      toast.error("Assegna un voto (da 1 a 5) a ogni domanda.");
      return;
    }
    setSubmittingFb(true);
    try {
      await api.post(`/me/courses/${courseId}/feedback`, { ratings, comment: comment.trim() || null });
      const f = await api.get(`/me/courses/${courseId}/feedback`);
      setFb(f.data);
      toast.success("Grazie per il tuo feedback!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmittingFb(false);
    }
  };

  const emit = async () => {
    setEmitting(true);
    try {
      const { data } = await api.post(`/me/courses/${courseId}/certificate`);
      setCert(data);
      toast.success("Attestato emesso con successo!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "profile_incomplete") {
        setMissing(detail.missing_fields || []);
        toast.error("Completa il profilo per emettere l'attestato");
      } else if (detail && typeof detail === "object" && detail.code === "feedback_required") {
        const f = await api.get(`/me/courses/${courseId}/feedback`);
        setFb(f.data);
        toast.error("Compila prima il questionario di gradimento");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setEmitting(false);
    }
  };

  const download = async () => {
    if (!cert) return;
    try {
      const res = await fetch(`${API_BASE}/me/certificates/${cert.id}/download`, {
        headers: { Authorization: `Bearer ${tokenStorage.get()}` },
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Certificato_${cert.last_name}_${cert.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Errore download");
    }
  };

  if (!finalPassed) return null;
  if (loading) return <div className="text-sm text-brand-muted"><Loader2 size={16} className="inline animate-spin mr-2" /> Verifica attestato…</div>;

  // Attestato già emesso
  if (cert) {
    return (
      <div data-testid="certificate-block" className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-2">
              <Award size={14} /> Attestato disponibile
            </div>
            <h2 className="text-2xl text-emerald-900 mb-1">Complimenti, hai completato il corso!</h2>
            <p className="text-sm text-emerald-800">
              Codice: <strong className="font-mono">{cert.code}</strong> · Emesso il {new Date(cert.issued_at).toLocaleDateString("it-IT")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={download} data-testid="cert-download-btn" className="btn-primary inline-flex items-center gap-2">
              <Download size={14} /> Scarica PDF
            </button>
            <a href={`/verifica/${cert.code}`} target="_blank" rel="noreferrer" data-testid="cert-verify-link"
              className="inline-flex items-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2 font-semibold text-sm">
              <ExternalLink size={14} /> Verifica online
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Profilo incompleto
  const mFields = missing || missingFields();
  const labelForField = { first_name: "Nome", last_name: "Cognome", fiscal_code: "Codice Fiscale", birth_date: "Data di nascita" };
  if (mFields.length > 0) {
    return (
      <div data-testid="cert-profile-incomplete" className="bg-amber-50 border border-amber-300 rounded-sm p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900 mb-1">Completa il tuo profilo per ottenere l'attestato</div>
            <p className="text-sm text-amber-800 mb-2">
              Mancano i seguenti campi obbligatori: {mFields.map((f) => labelForField[f] || f).join(", ")}.
            </p>
            <Link to="/profilo" data-testid="cert-goto-profile" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 hover:underline underline-offset-4">
              Vai al profilo <LinkIcon size={12} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Questionario da compilare prima dell'emissione
  if (fb && !fb.submitted) {
    return (
      <div data-testid="cert-feedback-block" className="bg-white border-2 border-brand-gold rounded-sm p-6 sm:p-8">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
          <ClipboardList size={14} /> Questionario di gradimento
        </div>
        <h2 className="text-2xl text-brand-ink mb-1">Un ultimo passaggio prima dell'attestato</h2>
        <p className="text-sm text-brand-muted mb-5">Aiutaci a migliorare il corso: valuta da 1 (per niente) a 5 (moltissimo). Poi potrai emettere l'attestato.</p>
        <div className="space-y-4">
          {(fb.questions || []).map((q, i) => (
            <div key={i}>
              <div className="text-sm text-brand-ink mb-1.5">{i + 1}. {q}</div>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setRatings((r) => { const n = [...r]; n[i] = v; return n; })}
                    className={"h-9 w-9 rounded-sm border text-sm font-semibold transition-colors " +
                      (ratings[i] === v ? "bg-brand-blue text-white border-brand-blue" : "border-brand-line text-brand-muted hover:border-brand-blue")}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <div className="text-sm text-brand-ink mb-1.5">Suggerimenti o commenti (facoltativo)</div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full min-h-[80px] rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              placeholder="Scrivi qui il tuo commento…"
            />
          </div>
          <button onClick={submitFeedback} disabled={submittingFb} data-testid="cert-feedback-submit" className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
            {submittingFb && <Loader2 size={14} className="animate-spin" />} Invia e prosegui
          </button>
        </div>
      </div>
    );
  }

  // Pronto per l'emissione (questionario compilato)
  return (
    <div data-testid="cert-emit-block" className="bg-white border-2 border-brand-gold rounded-sm p-6 sm:p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <Award size={14} /> Attestato pronto
          </div>
          <h2 className="text-2xl text-brand-ink mb-1">Emetti il tuo attestato</h2>
          <p className="text-sm text-brand-muted">Hai superato tutti i test e compilato il questionario. Emetti ora il tuo attestato di partecipazione.</p>
        </div>
        <button onClick={emit} disabled={emitting} data-testid="cert-emit-btn" className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
          {emitting && <Loader2 size={14} className="animate-spin" />}
          <Award size={14} /> Emetti attestato
        </button>
      </div>
    </div>
  );
}
